/**
 * Phase 107 (Plan 107-09 / D-10, D-11, D-14) — "New configuration" create form.
 *
 * Exactly three fields (Name, Currency, Base timezone) per 107-UI-SPEC.md's
 * Copywriting Contract. Deliberately carries no lead, project, customer or
 * owner field (D-11) and no configurator-type selector (D-02 — this is the
 * one concrete SLA configurator, with no registry and no abstraction
 * anticipating a second).
 *
 * Currency is chosen freely at creation (EUR/USD/CZK, default EUR). Per D-14
 * every rate inside a configuration lives in that one currency and there is
 * no conversion, no exchange rate and no CZK-equivalent anywhere in this
 * phase — the form offers no second currency field, no FX rate and no
 * comparison option, only the helper text below the select explaining the
 * one-way lock that fires once the configuration has bands (enforced
 * server-side by `slaPricingConfigurationService.ts`'s `updateConfiguration`,
 * T-107-CROSSCONFIG).
 */
import { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { CURATED_ZONES, DEFAULT_BASE_ZONE } from './tzUtils';
import type { CreateConfigurationInput } from '../../services/api/slaPricing';

const CURRENCIES = [
  'EUR',
  'USD',
  'CZK',
] as const;

export interface NewConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateConfigurationInput) => void;
  isSubmitting?: boolean;
}

export function NewConfigurationModal({
  isOpen,
  onClose,
  onCreate,
  isSubmitting = false,
}: NewConfigurationModalProps) {
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<string>(CURRENCIES[0]);
  const [baseTimezone, setBaseTimezone] = useState(DEFAULT_BASE_ZONE);

  const isValid = name.trim().length > 0;

  function reset() {
    setName('');
    setCurrency(CURRENCIES[0]);
    setBaseTimezone(DEFAULT_BASE_ZONE);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    onCreate({ name: name.trim(), currency, baseTimezone });
    reset();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New configuration"
      ariaLabelledBy="new-configuration-modal-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid} isLoading={isSubmitting}>
            Create
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <label className="mb-3 block text-[12px]">
          <span className="block font-medium text-gray-700">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={160}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px]"
            placeholder="Ahlsell variant A"
            autoFocus
          />
        </label>

        <label className="mb-1 block text-[12px]">
          <span className="block font-medium text-gray-700">Currency</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px]"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <p className="mb-3 text-[11px] text-gray-400">Currency locks once you add a band.</p>

        <label className="mb-1 block text-[12px]">
          <span className="block font-medium text-gray-700">Base timezone</span>
          <select
            value={baseTimezone}
            onChange={(e) => setBaseTimezone(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px]"
          >
            <option value={DEFAULT_BASE_ZONE}>{DEFAULT_BASE_ZONE}</option>
            {CURATED_ZONES.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label}
              </option>
            ))}
          </select>
        </label>
      </form>
    </Modal>
  );
}

export default NewConfigurationModal;
